import { ActionIcon, Badge, Group, Paper, Stack, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import { ReceivedCompletedCheckbox } from "../sticker/ReceivedCompletedCheckbox";

export interface StainOccurrence {
  id: string;
  selectionId: string;
  code: string;
  description: string;
  markAsCompleted: boolean;
  isDefault: boolean;
  isWorkList: boolean;
  panelSelectionKey?: string;
  panelComponentId?: string;
  panelName?: string;
}

interface StainOccurrenceRowProps {
  stain: StainOccurrence;
  slideIndex: number;
  isCompleted: boolean;
  canBePreProcessed: boolean;
  canRemove: boolean;
  removalDisabled: boolean;
  onCompletionChange: (selectionId: string, isCompleted: boolean) => void;
  onRemove: (stain: {
    stainId: string;
    selectionId: string;
    code: string;
  }) => void;
}

export default function StainOccurrenceRow({
  stain,
  slideIndex,
  isCompleted,
  canBePreProcessed,
  canRemove,
  removalDisabled,
  onCompletionChange,
  onRemove,
}: StainOccurrenceRowProps) {
  const removalLabel = useMemo(
    () =>
      removalDisabled
        ? "Cannot remove - at least 1 stain required"
        : "Remove stain",
    [removalDisabled]
  );
  const handleRemove = useCallback(() => {
    onRemove({
      stainId: stain.id,
      selectionId: stain.selectionId,
      code: stain.code,
    });
  }, [onRemove, stain.code, stain.id, stain.selectionId]);

  const handleCompletionToggle = useCallback(
    (value: boolean) => {
      onCompletionChange(stain.selectionId, value);
      return Promise.resolve();
    },
    [onCompletionChange, stain.selectionId]
  );

  return (
    <Paper p="xs" withBorder bg="gray.0">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs">
              <Text size="sm" fw={500}>
                Slide {slideIndex + 1}: {stain.code}
              </Text>
              {stain.isWorkList && (
                <Badge size="xs" variant="light" color="teal">
                  Work List
                </Badge>
              )}
              {stain.panelName && (
                <Badge size="xs" variant="light" color="blue">
                  {stain.panelName} · Locked
                </Badge>
              )}
              {!stain.isDefault && !stain.isWorkList && !stain.panelName && (
                <Badge size="xs" variant="light" color="green">
                  Added
                </Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {stain.description}
            </Text>
          </Stack>

          {canRemove && (
            <ActionIcon
              variant="subtle"
              color="red"
              size={44}
              onClick={handleRemove}
              aria-label={removalLabel}
              title={removalLabel}
              disabled={removalDisabled}
            >
              <IconX size={14} />
            </ActionIcon>
          )}
        </Group>

        {canBePreProcessed && (
          <ReceivedCompletedCheckbox
            isCompleted={isCompleted}
            canToggle
            onToggle={handleCompletionToggle}
          />
        )}
      </Stack>
    </Paper>
  );
}
